import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Coins, LogOutV2, Play, Settings } from "@piiqtechnologies/svg-icons";
import { PeekDivider, PeekList, PeekStack } from "@piiqtechnologies/ui-components";
import { usePolicyContext } from "@piiqtechnologies/ui-lib";
import { resolveRoute } from "@analytics-setup/routeTracking";
import { PATHS } from "@constants/Paths";
import { useOptionalActiveOrganization, useOrgPath } from "@stores/ActiveOrganizationContext";
import { DiscoverAction } from "@constants/Policies";
import { AnalyticsArea, useBillingEnabled, useIsEnterprise, useTrackEvent } from "@hooks";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin } from "@stores/utils";
import { StorylaneTourDialog } from "../../storylane-tour-dialog";
import { SIDEBAR_ICON_SIZE, SidebarPageItem } from "./SidebarPageItem";
import messages from "../messages.i18n";
export const SidebarFooter = () => {
    var _a, _b, _c;
    const intl = useIntl();
    const navigate = useNavigate();
    const billingEnabled = useBillingEnabled();
    const { onLogout, user } = useAuthContext();
    const canManageUsers = usePolicyContext(DiscoverAction.ManageUsers);
    const isPeekAdmin = getIsAdmin(user);
    const { pathname } = useLocation();
    const orgPath = useOrgPath();
    const track = useTrackEvent();
    const [isTourOpen, setIsTourOpen] = useState(false);
    const isPeekStaff = Boolean(((_a = user === null || user === void 0 ? void 0 : user.email) === null || _a === void 0 ? void 0 : _a.toLowerCase().endsWith("@peek.us")) && !((_b = user === null || user === void 0 ? void 0 : user.email) === null || _b === void 0 ? void 0 : _b.includes("+")));
    // See NavigationBar: `ManageBilling` is the membership's answer, the account role is not.
    const canManageBilling = usePolicyContext(DiscoverAction.ManageBilling);
    const canManagePlansBilling = billingEnabled && canManageBilling && !isPeekStaff;
    const activeOrgId = (_c = useOptionalActiveOrganization()) === null || _c === void 0 ? void 0 : _c.activeOrgId;
    const isEnterprise = useIsEnterprise(activeOrgId, canManagePlansBilling);
    const canAccessPlansBilling = canManagePlansBilling && !isEnterprise;
    const handleLogout = () => {
        onLogout();
        navigate(PATHS.LOGIN, { replace: true });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(PeekStack, { gap: 1 },
            React.createElement(PeekDivider, { orientation: "horizontal", light: true }),
            React.createElement(PeekList, { disablePadding: true },
                !isPeekAdmin && (React.createElement(SidebarPageItem, { onClick: () => setIsTourOpen(true), icon: React.createElement(Play, { width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }), label: intl.formatMessage(messages.appSidebarPlayDemo) })),
                canAccessPlansBilling && (React.createElement(SidebarPageItem, { to: orgPath(PATHS.PLANS_BILLING), icon: React.createElement(Coins, { width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }), label: intl.formatMessage(messages.appSidebarPlansBilling) })),
                canManageUsers && !isPeekStaff && (React.createElement(SidebarPageItem, { to: orgPath(PATHS.ACCOUNT_SETTINGS), 
                    // The event dev's nav bar fires for this entry; the row navigates and records it.
                    onClick: () => track(AnalyticsArea.UserSettings, "Clicked", { routeName: resolveRoute(pathname).routeName }), icon: React.createElement(Settings, { width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }), label: intl.formatMessage(messages.appSidebarAccountSettings) })),
                React.createElement(SidebarPageItem, { onClick: handleLogout, icon: React.createElement(LogOutV2, { width: SIDEBAR_ICON_SIZE, height: SIDEBAR_ICON_SIZE }), label: intl.formatMessage(messages.appSidebarLogout) }))),
        React.createElement(StorylaneTourDialog, { open: isTourOpen, onClose: () => setIsTourOpen(false), analyticsArea: AnalyticsArea.Sidebar, analyticsAction: "Tour", trackProps: { routeName: resolveRoute(pathname).routeName } })));
};
export default SidebarFooter;
